import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToWeekTransactions,
  convertPlannedToActual,
  deleteTransaction,
  getCurrentWeekId,
  formatCurrency,
} from '../../services/firebase';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CATEGORIES = [
  { id: 'food', label: 'Food', emoji: '🍕' },
  { id: 'groceries', label: 'Groceries', emoji: '🛒' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'entertainment', label: 'Fun', emoji: '🎬' },
  { id: 'clothing', label: 'Clothing', emoji: '👕' },
  { id: 'health', label: 'Health', emoji: '💊' },
  { id: 'books', label: 'Books', emoji: '📚' },
  { id: 'other', label: 'Other', emoji: '📦' },
];

const todayIndex = () => new Date().getDay();

const StudentReviewSpend = () => {
  const { family } = useAuth();
  const navigate = useNavigate();
  const weekId = getCurrentWeekId();

  const [transactions, setTransactions] = useState([]);   // overdue planned only
  const [allTransactions, setAllTransactions] = useState([]); // full week
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Item states: 'pending' | 'confirmed' | 'adjusted' | 'skipped'
  const [itemStates, setItemStates] = useState({});
  // Adjusted amounts per item id
  const [adjustedAmounts, setAdjustedAmounts] = useState({});
  // Which items are showing the adjust input
  const [adjustingId, setAdjustingId] = useState(null);

  useEffect(() => {
    if (!family?.id) return;
    // Subscribe to ALL week transactions for accurate recalculation
    const unsub = subscribeToWeekTransactions(family.id, weekId, (txs) => {
      setAllTransactions(txs);
      const todayDay = todayIndex();
      const overdue = txs.filter(t => {
        if (t.type !== 'planned') return false;
        if (t.spendDate === undefined) return false;
        const itemPos = t.spendDate === 0 ? 7 : t.spendDate;
        const todayPos = todayDay === 0 ? 7 : todayDay;
        return itemPos < todayPos;
      });
      setTransactions(overdue);
      setItemStates(prev => {
        const next = { ...prev };
        overdue.forEach(t => { if (!next[t.id]) next[t.id] = 'pending'; });
        return next;
      });
      setLoading(false);
    });
    return unsub;
  }, [family?.id, weekId]);

  const allResolved = transactions.length > 0 &&
    transactions.every(t => itemStates[t.id] && itemStates[t.id] !== 'pending');

  const resolvedCount = transactions.filter(
    t => itemStates[t.id] && itemStates[t.id] !== 'pending'
  ).length;

  const handleConfirm = (tx) => {
    setItemStates(prev => ({ ...prev, [tx.id]: 'confirmed' }));
    setAdjustingId(null);
  };

  const handleShowAdjust = (tx) => {
    setAdjustingId(tx.id);
    setAdjustedAmounts(prev => ({ ...prev, [tx.id]: tx.amount.toString() }));
  };

  const handleSaveAdjust = (tx) => {
    const val = parseFloat(adjustedAmounts[tx.id]);
    if (!val || val <= 0) return;
    setItemStates(prev => ({ ...prev, [tx.id]: 'adjusted' }));
    setAdjustingId(null);
  };

  const handleSkip = (tx) => {
    setItemStates(prev => ({ ...prev, [tx.id]: 'skipped' }));
    setAdjustingId(null);
  };

  const handleDone = async () => {
    if (!family?.id) return;
    setSaving(true);
    try {
      // Get all current transactions for recalculation at the end
      const confirmedTxs = transactions.filter(t =>
        itemStates[t.id] === 'confirmed' || itemStates[t.id] === 'adjusted'
      );
      const skippedTxs = transactions.filter(t => itemStates[t.id] === 'skipped');

      // Process each confirmed/adjusted item — convert planned to actual
      // Start from ALL week transactions so recalculation has full context
      let currentTxs = [...allTransactions];
      for (const tx of confirmedTxs) {
        const actualAmount = itemStates[tx.id] === 'adjusted'
          ? parseFloat(adjustedAmounts[tx.id]) || tx.amount
          : tx.amount;
        await convertPlannedToActual(family.id, weekId, tx, actualAmount, currentTxs, tx.spendDate);
        // Update local reference to reflect the conversion
        currentTxs = currentTxs.map(t => t.id === tx.id
          ? { ...t, type: 'actual', amount: actualAmount }
          : t
        );
      }

      // Delete skipped items
      for (const tx of skippedTxs) {
        await deleteTransaction(family.id, tx.id, currentTxs, weekId);
        currentTxs = currentTxs.filter(t => t.id !== tx.id);
      }

      setDone(true);
    } catch (err) {
      console.error('Review spend error:', err);
      setSaving(false);
    }
  };

  const cat = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[CATEGORIES.length - 1];

  // ── Done screen
  if (done) {
    const confirmedCount = transactions.filter(t =>
      itemStates[t.id] === 'confirmed' || itemStates[t.id] === 'adjusted'
    ).length;
    const skippedCount = transactions.filter(t => itemStates[t.id] === 'skipped').length;

    return (
      <div className="screen screen--no-nav">
        <div style={{ display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>✅</div>
          <h2 style={{ marginBottom: 12 }}>All caught up!</h2>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 8 }}>
            {confirmedCount > 0 && `${confirmedCount} item${confirmedCount > 1 ? 's' : ''} logged.`}
            {skippedCount > 0 && ` ${skippedCount} removed.`}
          </p>
          <p style={{ color: 'var(--color-text-3)', fontSize: '0.875rem',
            lineHeight: 1.5, marginBottom: 32 }}>
            Your spending totals have been updated.
          </p>
          <button className="btn btn--primary btn--full btn--lg"
            onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen screen--no-nav">
      {/* Header */}
      <button
        onClick={() => navigate('/')}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-text-2)', marginBottom: 20,
          fontSize: '0.9rem', display: 'flex', alignItems: 'center',
          gap: 6, padding: 0, fontFamily: 'var(--font-body)',
        }}>
        ← Back
      </button>

      <div className="page-header" style={{ marginBottom: 8 }}>
        <h1 className="page-title">Review spend</h1>
      </div>
      <p style={{ color: 'var(--color-text-2)', fontSize: '0.875rem',
        lineHeight: 1.6, marginBottom: 20 }}>
        These were planned but not logged yet. Did they happen?
      </p>

      {/* Progress bar */}
      {transactions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div className="progress-bar" style={{ flex: 1 }}>
            <div className="progress-bar__fill" style={{
              width: `${Math.round((resolvedCount / transactions.length) * 100)}%`,
              transition: 'width 300ms ease',
            }} />
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
            flexShrink: 0 }}>
            {resolvedCount} of {transactions.length} done
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div className="spinner" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>👍</div>
          <p style={{ color: 'var(--color-text-2)' }}>
            No overdue planned items — you're all caught up.
          </p>
          <button className="btn btn--primary btn--full"
            style={{ marginTop: 20 }} onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>
      ) : (
        <>
          <div className="stack stack-3" style={{ marginBottom: 24 }}>
            {transactions.map(tx => {
              const state = itemStates[tx.id] || 'pending';
              const category = cat(tx.category);
              const isResolved = state !== 'pending';
              const isAdjusting = adjustingId === tx.id;

              return (
                <div key={tx.id} className="card" style={{
                  opacity: isResolved ? 0.55 : 1,
                  transition: 'opacity 300ms ease',
                  padding: 0, overflow: 'hidden',
                }}>
                  {/* Item header */}
                  <div style={{ display: 'flex', alignItems: 'center',
                    gap: 12, padding: '14px 16px 12px' }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: isResolved ? 'var(--color-surface-2)' : 'var(--color-primary-pale)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18,
                    }}>
                      {category.emoji}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9375rem',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.note || category.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 1 }}>
                        {DAYS[tx.spendDate]} · {category.label}
                      </div>
                    </div>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: '1.1rem', flexShrink: 0,
                      color: isResolved ? 'var(--color-text-3)' : 'var(--color-text)',
                    }}>
                      {state === 'adjusted'
                        ? formatCurrency(parseFloat(adjustedAmounts[tx.id]) || tx.amount)
                        : formatCurrency(tx.amount)}
                    </div>
                  </div>

                  {/* Resolved badge */}
                  {isResolved && (
                    <div style={{
                      padding: '4px 16px 12px',
                      fontSize: '0.8125rem', fontWeight: 600,
                      color: state === 'skipped' ? 'var(--color-text-3)' : 'var(--color-primary)',
                    }}>
                      {state === 'confirmed' && `✓ Logged ${formatCurrency(tx.amount)}`}
                      {state === 'adjusted' && `✓ Logged ${formatCurrency(parseFloat(adjustedAmounts[tx.id]) || tx.amount)}`}
                      {state === 'skipped' && '— Removed from plan'}
                    </div>
                  )}

                  {/* Adjust input */}
                  {!isResolved && isAdjusting && (
                    <div style={{ padding: '0 16px 14px',
                      display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-2)',
                        flexShrink: 0 }}>Actual amount:</span>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <span style={{ position: 'absolute', left: 12, top: '50%',
                          transform: 'translateY(-50%)', color: 'var(--color-text-2)',
                          fontSize: '0.9rem' }}>$</span>
                        <input
                          type="number" min="0.01" step="0.01"
                          value={adjustedAmounts[tx.id] || ''}
                          onChange={e => setAdjustedAmounts(prev =>
                            ({ ...prev, [tx.id]: e.target.value }))}
                          autoFocus
                          style={{
                            width: '100%', padding: '8px 12px 8px 24px',
                            border: '1.5px solid var(--color-primary)',
                            borderRadius: 'var(--radius-md)',
                            fontFamily: 'var(--font-display)', fontWeight: 700,
                            fontSize: '1rem', outline: 'none',
                            background: 'var(--color-surface)',
                          }}
                        />
                      </div>
                      <button
                        className="btn btn--primary"
                        style={{ padding: '8px 16px', fontSize: '0.875rem', flexShrink: 0 }}
                        onClick={() => handleSaveAdjust(tx)}>
                        Save
                      </button>
                    </div>
                  )}

                  {/* Action buttons */}
                  {!isResolved && !isAdjusting && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 8, padding: '0 16px 14px' }}>
                      <button
                        onClick={() => handleConfirm(tx)}
                        style={{
                          padding: '9px 4px', borderRadius: 'var(--radius-md)',
                          border: '1.5px solid var(--color-primary)',
                          background: 'var(--color-primary-pale)',
                          color: 'var(--color-primary)', fontWeight: 600,
                          fontSize: '0.75rem', cursor: 'pointer',
                          fontFamily: 'var(--font-body)',
                        }}>
                        ✓ Yes, {formatCurrency(tx.amount)}
                      </button>
                      <button
                        onClick={() => handleShowAdjust(tx)}
                        style={{
                          padding: '9px 4px', borderRadius: 'var(--radius-md)',
                          border: '1.5px solid var(--color-accent)',
                          background: 'var(--color-accent-light)',
                          color: '#C45E1A', fontWeight: 600,
                          fontSize: '0.75rem', cursor: 'pointer',
                          fontFamily: 'var(--font-body)',
                        }}>
                        Edit amount
                      </button>
                      <button
                        onClick={() => handleSkip(tx)}
                        style={{
                          padding: '9px 4px', borderRadius: 'var(--radius-md)',
                          border: '1.5px solid var(--color-border)',
                          background: 'var(--color-surface)',
                          color: 'var(--color-text-2)', fontWeight: 500,
                          fontSize: '0.75rem', cursor: 'pointer',
                          fontFamily: 'var(--font-body)',
                        }}>
                        Didn't happen
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Done button */}
          <button
            className="btn btn--primary btn--full btn--lg"
            onClick={handleDone}
            disabled={!allResolved || saving}>
            {saving
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : allResolved
                ? `Done — update ${resolvedCount} item${resolvedCount > 1 ? 's' : ''} ✓`
                : `${transactions.length - resolvedCount} item${transactions.length - resolvedCount > 1 ? 's' : ''} remaining`}
          </button>
        </>
      )}
    </div>
  );
};

export default StudentReviewSpend;
